import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { SelectCadastroRh } from "./SelectCadastroRh";

/** Item do combo `GET /api/contas-correntes` (só contas ativas). */
export interface ContaCorrenteOpcao {
  id: number;
  descricao: string | null;
  bancoId: number | null;
  codigoBanco?: string | null;
  bancoNome?: string | null;
  agencia?: string | null;
  numeroConta?: string | null;
}

/** Contas correntes ativas, com cache compartilhado entre as linhas/telas. */
export function useContasCorrentesCombo() {
  return useQuery({
    queryKey: ["contas-correntes-combo"],
    queryFn: async () => (await apiClient.get<ContaCorrenteOpcao[]>("/api/contas-correntes")).data,
    staleTime: 60_000,
  });
}

/** "Banco · Ag 1234 · CC 5678-9 — descrição" (partes ausentes são omitidas). */
export function rotuloContaCorrente(c: ContaCorrenteOpcao) {
  const banco = c.bancoNome || (c.codigoBanco ? `Banco ${c.codigoBanco}` : null);
  const partes = [banco, c.agencia && `Ag ${c.agencia}`, c.numeroConta && `CC ${c.numeroConta}`].filter(Boolean);
  const dados = partes.join(" · ");
  if (!dados) return c.descricao || `Conta ${c.id}`;
  return c.descricao ? `${dados} — ${c.descricao}` : dados;
}

interface ContaCorrenteSelectProps {
  /** null = nenhuma conta. */
  value: number | null;
  onChange: (v: number | null) => void;
  /** Rótulo da opção "nenhuma". `null` esconde a opção. */
  rotuloNenhum?: string | null;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/** Seletor de conta corrente da empresa por banco/agência/conta (lista curta, sem busca). */
export function ContaCorrenteSelect({
  value, onChange, rotuloNenhum = "Nenhuma", placeholder = "Selecione a conta corrente", ...props
}: ContaCorrenteSelectProps) {
  const { data = [], isLoading } = useContasCorrentesCombo();
  const opcoes = data.map((c) => ({ id: c.id, rotulo: rotuloContaCorrente(c), ativo: true }));
  return (
    <SelectCadastroRh
      {...props}
      value={value ?? 0}
      onChange={(v) => onChange(v ? v : null)}
      rotuloNenhum={rotuloNenhum}
      placeholder={isLoading ? "Carregando contas…" : placeholder}
      opcoes={opcoes}
    />
  );
}
